import { debounce, upperFirst } from "lodash-es";
import { defineStore, storeToRefs } from "pinia";
import { type Scope, type WhereClause } from "spraypaint";
import { computed, type Ref, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { getLocalePluralText, getLocaleText } from "shared/boot/i18n";
import { type BasePaginationData } from "shared/components/base/BasePagination";
import { useNotify } from "shared/composables/useNotify";
import useTeamPicker from "shared/composables/useTeamPicker";
import { safePush } from "shared/helpers/routing";
import { useFavorites } from "shared/modules/Insights/composables/actions/useFavorites";
import { useInsightsActions } from "shared/modules/Insights/composables/actions/useInsightsActions";
import { useInsightsShareReport } from "shared/modules/Insights/composables/actions/useInsightsShareReport";
import { useInsightsStore } from "shared/modules/Insights/stores/useInsightsStore";
import {
  type BaseInsightsResponse,
  InsightsAction,
  type InsightsLandingPageItemTarget,
  type InsightsLandingPageItemTargetString,
  InsightsLandingPageType,
  type InsightsLandingPageTypeMap,
} from "shared/modules/Insights/types";
import { isInsightsArchived } from "shared/modules/Insights/types/archived/guards";
import {
  isFavorite,
  isInstantInsightsReport,
  isLandingPageItemTarget,
  isStreamReport,
} from "shared/modules/Insights/types/guards";
import { useInsightsDashboardActions } from "shared/modules/InsightsDashboard/composables/dashboard/useInsightsDashboardActions";
import { useInsightsDashboardRoutes } from "shared/modules/InsightsDashboard/composables/dashboard/useInsightsDashboardRoutes";
import { useOrganisationUsageStore } from "shared/modules/OrganisationUsage/stores/useOrganisationUsageStore";
import {
  Favorite,
  InsightsArchived,
  InstantInsightsReport,
  StreamReport,
} from "shared/resources";
import { StreamReportStatus } from "shared/resources/StreamReport";
import FavoriteService from "shared/services/api/FavoriteService";
import InsightsArchivedService from "shared/services/api/InsightsArchivedService";
import InstantInsightsReportService from "shared/services/api/InstantInsightsReportService";
import StreamReportService from "shared/services/api/StreamReportService";
import features from "shared/services/features";
import { type AsyncData, type SortOption } from "shared/types";

export const RECENT_ITEMS_LIMIT = 5;

export interface LandingPageConfig {
  loadRecentItems?: boolean;
  searchKey: string;
  sortOptions: SortOption[];
}

export const INSIGHTS_DASHBOARDS_SORT_OPTIONS: SortOption[] = [
  {
    direction: "desc",
    label: getLocaleText("insights_dashboards_table.last_edited"),
    value: "updatedAt",
  },
  {
    direction: "asc",
    label: getLocaleText("global.name"),
    value: "name",
  },
  {
    direction: "asc",
    label: getLocaleText("insights_dashboards_table.owner"),
    value: "user.first_name",
  },
  {
    direction: "desc",
    label: getLocaleText("insights_dashboards_table.applied_date_range"),
    value: "after",
  },
] as const;

export const INSIGHTS_DOWNLOADS_SORT_OPTIONS: SortOption[] = [
  {
    direction: "desc",
    label: getLocaleText("insights_downloads.most_recent"),
    value: "id",
  },
  {
    direction: "asc",
    label: getLocaleText("global.name"),
    value: "label",
  },
] as const;

export const INSIGHTS_STARRED_SORT_OPTIONS: SortOption[] = [
  {
    direction: "desc",
    label: getLocaleText("insights_starred.most_recent"),
    value: "createdAt",
  },
  {
    direction: "asc",
    label: getLocaleText("global.name"),
    value: "name",
  },
] as const;

export const INSIGHTS_SHARED_SORT_OPTIONS: SortOption[] = [
  {
    direction: "desc",
    label: getLocaleText("insights_shared.last_edited"),
    value: "updatedAt",
  },
  {
    direction: "asc",
    label: getLocaleText("global.name"),
    value: "name",
  },
] as const;

export const INSIGHTS_ARCHIVED_SORT_OPTIONS: SortOption[] = [
  {
    direction: "desc",
    label: getLocaleText("insights_archived.most_recent"),
    value: "archivedAt",
  },
  {
    direction: "asc",
    label: getLocaleText("global.name"),
    value: "name",
  },
] as const;

export const LANDING_PAGE_CONFIG: Record<
  InsightsLandingPageType,
  LandingPageConfig
> = {
  [InsightsLandingPageType.Archived]: {
    searchKey: "name",
    sortOptions: INSIGHTS_ARCHIVED_SORT_OPTIONS,
  },
  [InsightsLandingPageType.Dashboards]: {
    loadRecentItems: true,
    searchKey: "name",
    sortOptions: INSIGHTS_DASHBOARDS_SORT_OPTIONS,
  },
  [InsightsLandingPageType.Downloads]: {
    searchKey: "label",
    sortOptions: INSIGHTS_DOWNLOADS_SORT_OPTIONS,
  },
  [InsightsLandingPageType.Shared]: {
    searchKey: "name",
    sortOptions: INSIGHTS_SHARED_SORT_OPTIONS,
  },
  [InsightsLandingPageType.Starred]: {
    searchKey: "name",
    sortOptions: INSIGHTS_STARRED_SORT_OPTIONS,
  },
} as const;

export const useInsightsLandingPageStoreFactory = <
  T extends InsightsLandingPageType,
>(
  type: T
) => {
  type InsightsLandingPageResponse = BaseInsightsResponse<LandingPageItem>;

  type LandingPageItem = InsightsLandingPageTypeMap[T];
  type LandingPageData = AsyncData<LandingPageItem[]>;
  type RequiredLandingPageData = Required<LandingPageData>;

  const { loadRecentItems, searchKey, sortOptions } = LANDING_PAGE_CONFIG[type];
  const DEFAULT_SORT_OPTION = sortOptions[0];

  return defineStore(`insightsLandingPage${upperFirst(type)}`, () => {
    const insightsActions = useInsightsActions();

    const {
      disabledActions,
      getInsightsActionHandler,
      loadingActions,
      removeDisabledAction,
    } = insightsActions;

    const dashboardActions = useInsightsDashboardActions(insightsActions);

    const { bulkShareInsightsReports } = useInsightsShareReport();

    const { bulkAddFavorites, toggleFavorite } = useFavorites();

    const { successNotification } = useNotify();
    const router = useRouter();
    const safeRouterPush = safePush({ router });

    const { selectedTeamPreference } = useTeamPicker();

    const { search } = storeToRefs(useInsightsStore());
    const { loadOrganisationUsage } = useOrganisationUsageStore();
    const { buildInsightsDashboardRouteName } = useInsightsDashboardRoutes();

    const editable = ref<boolean>(false);
    const selectedItems = ref<RequiredLandingPageData["selectAllIds"]>([]);
    const landingPageItemsAbortController = ref<AbortController>();
    const dashboardTab = ref<"all" | "monitoring">("all");

    // Cached recent items - populated from initial load only
    const recentLandingPageItemsCache = ref<LandingPageItem[]>([]);

    const initLandingPageItems = (): RequiredLandingPageData => ({
      data: [],
      initialLoad: false,
      isLoading: true,
      pagination: {
        page: 1,
        pageCount: 0,
        pageSize: 20,
        totalItems: 0,
      },
      query: {},
      selectAllIds: [],
      sortBy: DEFAULT_SORT_OPTION,
    });

    const landingPageItems = ref(
      initLandingPageItems()
    ) as Ref<RequiredLandingPageData>;

    // Computed property that provides AsyncData interface for recentLandingPageItems
    // Uses cached data from initial load, loading state from landingPageItems
    const recentLandingPageItems = computed<LandingPageData>(() => ({
      data: recentLandingPageItemsCache.value as LandingPageItem[],
      initialLoad: landingPageItems.value.initialLoad,
      isLoading: !landingPageItems.value.initialLoad,
      selectAllIds: [],
    }));

    const selectedItemsMapped = computed<
      number[] | InsightsLandingPageItemTarget[]
    >(() => {
      if (
        [
          InsightsLandingPageType.Starred,
          InsightsLandingPageType.Archived,
        ].includes(type)
      ) {
        return selectedItems.value.map((id) => {
          const [targetType, targetId] = id.toString().split("_");

          return {
            id: Number(targetId),
            type: targetType as InsightsLandingPageItemTargetString,
          };
        });
      }

      return selectedItems.value.map((id) => Number(id));
    });

    const downloadsData = computed<StreamReport[]>(() =>
      landingPageItems.value.data
        .filter((item) => {
          if (isFavorite(item)) {
            return isStreamReport(item.target);
          }

          return isStreamReport(item);
        })
        .map((item) => item as StreamReport)
    );

    const downloadsProcessing = computed<StreamReport[]>(() =>
      downloadsData.value.filter((report) =>
        [
          StreamReportStatus.PROCESSING,
          StreamReportStatus.HTML_COMPLETED,
        ].includes(report.status)
      )
    );

    function resetEditState(): void {
      editable.value = false;
    }

    function getTypeText(count: number = 1): string {
      return getLocalePluralText(`insights_landing_page.${type}`, count);
    }

    function isAbortError(error: any): boolean {
      return error?.originalError?.name === "AbortError";
    }

    function getItemSelectAllId(item: LandingPageItem): string | number {
      if (isFavorite(item)) {
        return `${item.targetType}_${item.targetId}`;
      }

      return Number(item.id);
    }

    function downloadsScope(): Scope<LandingPageItem> {
      return StreamReport.includes(["favorite"]) as Scope<LandingPageItem>;
    }

    function instantInsightsReportScope(): Scope<LandingPageItem> {
      return InstantInsightsReport.withUserAccessibles().includes([
        "user",
        "favorite",
      ]) as Scope<LandingPageItem>;
    }

    function sharedScope(): Scope<LandingPageItem> {
      const sharedWithOthers =
        landingPageItems.value.query.sharedWithOthers || false;

      if (sharedWithOthers) {
        return instantInsightsReportScope().where({
          shared_with_others: true,
        }) as Scope<LandingPageItem>;
      }

      return instantInsightsReportScope().where({
        shared_with_user_only: true,
      }) as Scope<LandingPageItem>;
    }

    function starredScope(): Scope<LandingPageItem> {
      return Favorite.includes([{ target: "favorite" }]).where({
        targetType:
          landingPageItems.value.query.targetType || "InstantInsightsReport",
      }) as Scope<LandingPageItem>;
    }

    function archivedScope(): Scope<LandingPageItem> {
      return InsightsArchived.includes(["target"]).where({
        targetType:
          landingPageItems.value.query.targetType || "InstantInsightsReport",
      }) as Scope<LandingPageItem>;
    }

    function getBaseScope(): Scope<LandingPageItem> {
      if (type === InsightsLandingPageType.Downloads) {
        return downloadsScope();
      }

      if (type === InsightsLandingPageType.Starred) {
        return starredScope();
      }

      if (type === InsightsLandingPageType.Archived) {
        return archivedScope();
      }

      if (type === InsightsLandingPageType.Shared) {
        return sharedScope();
      }

      return instantInsightsReportScope();
    }

    async function fetchLandingPageItems(
      pagination: BasePaginationData,
      sortBy: SortOption = DEFAULT_SORT_OPTION,
      query: WhereClause = {},
      extraFetchOptions: RequestInit = {}
    ): Promise<InsightsLandingPageResponse> {
      const request = getBaseScope()
        .extraFetchOptions(extraFetchOptions)
        .where({
          ...(features.has("has_team_workspaces")
            ? { organisationTeamId: [selectedTeamPreference.value, "null"] }
            : {}),
          ...query,
        })
        .order({ [sortBy.value]: sortBy.direction })
        .page(pagination.page)
        .per(pagination.pageSize)
        .stats({ select_all: "ids", total: "count" })
        .all();

      const { data, meta } = await request;

      return {
        data,
        pagination: {
          ...pagination,
          pageCount: Math.ceil(meta.stats.total.count / pagination.pageSize),
          totalItems: meta.stats.total.count,
        },
        selectAllIds: meta.stats.select_all.ids,
      };
    }

    async function handleLoadLandingPageItems(): Promise<void> {
      try {
        if (landingPageItemsAbortController.value) {
          landingPageItemsAbortController.value.abort();
        }

        landingPageItemsAbortController.value = new AbortController();

        landingPageItems.value.isLoading = true;

        const { data, pagination, selectAllIds } = await fetchLandingPageItems(
          landingPageItems.value.pagination,
          landingPageItems.value.sortBy,
          {
            ...(search.value ? { [searchKey]: { match: search.value } } : {}),
            ...(dashboardTab.value === "monitoring"
              ? { has_ai_visibility_project: true }
              : {}),
          },
          {
            signal: landingPageItemsAbortController.value.signal,
          }
        );

        // Cache recent items only on initial load with default sort and no search
        // This avoids making a separate API call for recent dashboards
        const isDefaultSort =
          landingPageItems.value.sortBy.value === DEFAULT_SORT_OPTION.value &&
          landingPageItems.value.sortBy.direction ===
            DEFAULT_SORT_OPTION.direction;

        const isFirstPage = landingPageItems.value.pagination.page === 1;
        const hasNoSearch = !search.value;

        const shouldCacheRecent =
          loadRecentItems && isDefaultSort && isFirstPage && hasNoSearch;

        if (shouldCacheRecent) {
          recentLandingPageItemsCache.value = data.slice(0, RECENT_ITEMS_LIMIT);
        }

        landingPageItems.value = {
          ...landingPageItems.value,
          data,
          initialLoad: true,
          isLoading: false,
          pagination,
          selectAllIds,
        };
      } catch (error: any) {
        if (isAbortError(error)) {
          return;
        }

        landingPageItems.value.isLoading = false;

        ErrorDialog(
          getLocaleText("insights_landing_page.error_loading_items", {
            type: getTypeText(2),
          }),
          error
        );
      }
    }

    async function rehandleLoadLandingPageItems(
      updatedIds: LandingPageData["selectAllIds"] = [],
      includesNewItem: boolean = false
    ): Promise<void> {
      // Check if we need to update recent items cache after reloading
      const shouldUpdateRecentCache =
        loadRecentItems &&
        (includesNewItem ||
          recentLandingPageItems.value.data.some((item) =>
            updatedIds.includes(getItemSelectAllId(item))
          ));

      await handleLoadLandingPageItems();

      // If recent items were affected and we're on default sort,
      // update the cache from the fresh data
      if (shouldUpdateRecentCache) {
        const isDefaultSort =
          landingPageItems.value.sortBy.value === DEFAULT_SORT_OPTION.value &&
          landingPageItems.value.sortBy.direction ===
            DEFAULT_SORT_OPTION.direction;

        const isFirstPage = landingPageItems.value.pagination.page === 1;
        const hasNoSearch = !search.value;

        if (isDefaultSort && isFirstPage && hasNoSearch) {
          recentLandingPageItemsCache.value = landingPageItems.value.data.slice(
            0,
            RECENT_ITEMS_LIMIT
          );
        }
      }
    }

    const handleSearchLandingPageItems = debounce(
      handleLoadLandingPageItems,
      250
    );

    function handleUpdatePagination(
      pagination?: Required<BasePaginationData>
    ): void {
      if (pagination) {
        landingPageItems.value.pagination = pagination;
        handleSearchLandingPageItems();
      }
    }

    function handleUpdateSortBy(value?: SortOption): void {
      if (value) {
        landingPageItems.value.sortBy = value;
        handleSearchLandingPageItems();
      }
    }

    function handleUpdateQuery(value: RequiredLandingPageData["query"]): void {
      landingPageItems.value.query = value;
      handleSearchLandingPageItems();
    }

    function updateLandingPageItem(updatedItem: LandingPageItem): void {
      const index = landingPageItems.value.data.findIndex(
        (item) => Number(item.id) === Number(updatedItem.id)
      );

      const recentIndex = recentLandingPageItemsCache.value.findIndex(
        (item) => Number(item.id) === Number(updatedItem.id)
      );

      if (index !== -1) {
        landingPageItems.value.data.splice(index, 1, updatedItem);
      }

      if (recentIndex !== -1) {
        recentLandingPageItemsCache.value.splice(
          recentIndex,
          1,
          updatedItem as (typeof recentLandingPageItemsCache.value)[number]
        );
      }
    }

    async function handleRegenerate(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isStreamReport(target)) {
        return;
      }

      const updatedIds = [Number(target.id)];
      const typeText = getTypeText();

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Regenerate,
        disableAction: true,
        errorMessage: getLocaleText("insights_landing_page.regenerate_error", {
          type: typeText,
        }),
        ids: updatedIds,
        loadingMessage: getLocaleText(
          "insights_landing_page.regenerate_loading",
          {
            type: typeText,
          }
        ),
        preserveDisabledAction: true,
        successMessage: getLocaleText(
          "insights_landing_page.regenerate_success",
          {
            type: typeText,
          }
        ),
      });

      await actionHandler(async () => {
        await StreamReportService.regenerate(Number(target.id));
        await rehandleLoadLandingPageItems(updatedIds);
      });
    }

    async function handleBulkRegenerate(): Promise<void> {
      if (type !== InsightsLandingPageType.Downloads) {
        return;
      }

      const typeText = getTypeText(selectedItems.value.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Regenerate,
        disableAction: true,
        errorMessage: getLocaleText("insights_landing_page.regenerate_error", {
          type: typeText,
        }),
        ids: selectedItems.value,
        loadingMessage: getLocaleText(
          "insights_landing_page.regenerate_loading",
          {
            type: typeText,
          }
        ),
        preserveDisabledAction: true,
        successMessage: getLocaleText(
          "insights_landing_page.regenerate_success",
          {
            type: typeText,
          }
        ),
      });

      await actionHandler(async () => {
        await StreamReportService.bulkRegenerate(
          selectedItemsMapped.value as number[]
        );

        resetEditState();

        await rehandleLoadLandingPageItems(selectedItems.value);
      });
    }

    async function handleArchive(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!(isStreamReport(target) || isInstantInsightsReport(target))) {
        return;
      }

      const updatedIds = [Number(target.id)];
      const typeText = getTypeText();

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Archive,
        confirmDialog: {
          confirmLabel: getLocaleText("global.archive"),
          message: getLocaleText(
            "insights_landing_page.confirm_archive_message"
          ),
          title: getLocaleText("insights_landing_page.confirm_archive_title"),
        },
        errorMessage: getLocaleText("insights_landing_page.archive_error", {
          type: typeText,
        }),
        ids: updatedIds,
        loadingMessage: getLocaleText("insights_landing_page.archive_loading", {
          type: typeText,
        }),
        successMessage: getLocaleText("insights_landing_page.archive_success", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        if (isStreamReport(target)) {
          await StreamReportService.archive(Number(target.id));
        } else {
          await InstantInsightsReportService.archive(Number(target.id));
        }

        await loadOrganisationUsage();
        await rehandleLoadLandingPageItems(updatedIds);
      });
    }

    async function handleBulkArchive(): Promise<void> {
      const typeText = getTypeText(selectedItems.value.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Archive,
        confirmDialog: {
          confirmLabel: getLocaleText("global.archive"),
          message: getLocalePluralText(
            "insights_landing_page.confirm_archive_message",
            selectedItems.value.length,
            {
              type: typeText,
            }
          ),
          title: getLocaleText("insights_landing_page.confirm_archive_title"),
        },
        errorMessage: getLocaleText("insights_landing_page.archive_error", {
          type: typeText,
        }),
        ids: selectedItems.value,
        loadingMessage: getLocaleText("insights_landing_page.archive_loading", {
          type: typeText,
        }),
        successMessage: getLocaleText("insights_landing_page.archive_success", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        const ids = selectedItemsMapped.value;

        if (type === InsightsLandingPageType.Downloads) {
          await StreamReportService.bulkArchive(ids as number[]);
        } else if (type === InsightsLandingPageType.Starred) {
          await FavoriteService.bulkArchive(
            ids as InsightsLandingPageItemTarget[]
          );
        } else {
          await InstantInsightsReportService.bulkArchive(ids as number[]);
        }

        await loadOrganisationUsage();
        resetEditState();
        await rehandleLoadLandingPageItems(selectedItems.value);
      });
    }

    async function handleBulkFavorite(
      favoriteType: InsightsLandingPageItemTargetString
    ): Promise<void> {
      if (
        [
          InsightsLandingPageType.Archived,
          InsightsLandingPageType.Starred,
        ].includes(type)
      ) {
        return;
      }

      const typeText = getTypeText(selectedItems.value.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Star,
        errorMessage: getLocaleText("insights_landing_page.star_error", {
          type: typeText,
        }),
        ids: selectedItems.value,
        loadingMessage: getLocaleText("insights_landing_page.star_loading", {
          type: typeText,
        }),
        successMessage: getLocaleText("insights_landing_page.star_success", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        await bulkAddFavorites(
          favoriteType,
          selectedItemsMapped.value as number[]
        );

        resetEditState();

        await rehandleLoadLandingPageItems(selectedItems.value);
      });
    }

    async function handleBulkRemoveFavorite(
      favoriteType?: InsightsLandingPageItemTargetString
    ): Promise<void> {
      if (type === InsightsLandingPageType.Archived) {
        return;
      }

      if (type !== InsightsLandingPageType.Starred && !favoriteType) {
        return;
      }

      const typeText = getTypeText(selectedItems.value.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Star,
        errorMessage: getLocaleText("insights_landing_page.unstar_error", {
          type: typeText,
        }),
        ids: selectedItems.value,
        loadingMessage: getLocaleText("insights_landing_page.unstar_loading", {
          type: typeText,
        }),
        successMessage: getLocaleText("insights_landing_page.unstar_success", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        let ids = selectedItemsMapped.value;

        if (type !== InsightsLandingPageType.Starred) {
          ids = (selectedItemsMapped.value as number[]).map(
            (id) =>
              ({
                id,
                type: favoriteType!,
              }) satisfies InsightsLandingPageItemTarget
          );
        }

        await FavoriteService.bulkRemoveFavorite(
          ids as InsightsLandingPageItemTarget[]
        );

        resetEditState();

        await rehandleLoadLandingPageItems(selectedItems.value);
      });
    }

    async function handleToggleFavorite(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isLandingPageItemTarget(target)) {
        return;
      }

      const typeText = getTypeText();

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Star,
        errorMessage: getLocaleText("insights_landing_page.star_error", {
          type: typeText,
        }),
        ids: [Number(target.id)],
        loadingMessage: getLocaleText("insights_landing_page.star_loading", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        const updatedItem = await toggleFavorite(target);

        const hasFavorite = Boolean(updatedItem.favorite);

        const successMessage = hasFavorite
          ? getLocaleText("insights_landing_page.star_success", {
              type: typeText,
            })
          : getLocaleText("insights_landing_page.unstar_success", {
              type: typeText,
            });

        successNotification({ message: successMessage });

        if (!isFavorite(item)) {
          updateLandingPageItem(updatedItem as LandingPageItem);
        }
      });
    }

    async function handleGeneratePdf(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isInstantInsightsReport(target)) {
        return;
      }

      await dashboardActions.handleGenerateDashboardPdf(target);
    }

    async function handleGenerateXlsx(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isInstantInsightsReport(target)) {
        return;
      }

      await dashboardActions.handleGenerateDashboardXlsx(target);
    }

    async function handleGenerateScheduledReport(
      item: LandingPageItem
    ): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isInstantInsightsReport(target)) {
        return;
      }

      await safeRouterPush({
        name: buildInsightsDashboardRouteName({
          path: "dashboard",
          type,
        }),
        params: { id: target.id },
        query: { action: InsightsAction.GenerateScheduledReport },
      });
    }

    async function handleShare(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isInstantInsightsReport(target)) {
        return;
      }

      await dashboardActions.handleShareDashboard(target);
    }

    async function handleBulkShare(): Promise<void> {
      if (type !== InsightsLandingPageType.Dashboards) {
        return;
      }

      const ids = selectedItemsMapped.value as number[];

      const typeText = getTypeText(ids.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Share,
        errorMessage: getLocaleText("insights_landing_page.share_error", {
          type: typeText,
        }),
        ids,
        loadingMessage: getLocaleText("insights_landing_page.share_loading", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        const message = await bulkShareInsightsReports(ids);

        if (message) {
          successNotification({ message });
          rehandleLoadLandingPageItems(ids);
        }
      });

      editable.value = false;
    }

    async function handleDuplicate(item: LandingPageItem): Promise<void> {
      const target = isFavorite(item) ? item.target : item;

      if (!isInstantInsightsReport(target)) {
        return;
      }

      const duplicated =
        await dashboardActions.handleDuplicateDashboard(target);

      if (duplicated) {
        await rehandleLoadLandingPageItems([Number(target.id)], true);
      }
    }

    async function handleBulkDuplicate(): Promise<void> {
      if (type !== InsightsLandingPageType.Dashboards) {
        return;
      }

      const ids = selectedItemsMapped.value as number[];

      const typeText = getTypeText(ids.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Duplicate,
        errorMessage: getLocaleText("insights_landing_page.duplicate_error", {
          type: typeText,
        }),
        ids,
        loadingMessage: getLocaleText(
          "insights_landing_page.duplicate_loading",
          {
            type: typeText,
          }
        ),
        successMessage: getLocaleText(
          "insights_landing_page.duplicate_success",
          {
            type: typeText,
          }
        ),
      });

      await actionHandler(async () => {
        await InstantInsightsReportService.bulkDuplicate(
          ids,
          selectedTeamPreference.value || null
        );

        rehandleLoadLandingPageItems(ids, true);
      });

      editable.value = false;
    }

    async function handleDelete(item: LandingPageItem): Promise<void> {
      const target = isInsightsArchived(item) ? item.target : item;

      if (!isLandingPageItemTarget(target)) {
        return;
      }

      const typeText = getTypeText();

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Delete,
        confirmDialog: {
          confirmLabel: getLocaleText("global.delete"),
          message: getLocaleText(
            "insights_landing_page.confirm_delete_message",
            {
              type: typeText,
            }
          ),
          title: getLocaleText("insights_landing_page.confirm_delete_title", {
            type: typeText,
          }),
        },
        errorMessage: getLocaleText("insights_landing_page.delete_error", {
          type: typeText,
        }),
        ids: [Number(target.id)],
        loadingMessage: getLocaleText("insights_landing_page.delete_loading", {
          type: typeText,
        }),
        successMessage: getLocaleText("insights_landing_page.delete_success", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        await InsightsArchivedService.remove({
          id: Number(target.id),
          type:
            target instanceof StreamReport
              ? "StreamReport"
              : "InstantInsightsReport",
        });

        await rehandleLoadLandingPageItems([Number(target.id)]);
      });
    }

    async function handleBulkDelete(): Promise<void> {
      const typeText = getTypeText(selectedItems.value.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Delete,
        confirmDialog: {
          confirmLabel: getLocaleText(
            "insights_landing_page.confirm_delete_label"
          ),
          message: getLocalePluralText(
            "insights_landing_page.confirm_delete_message",
            selectedItems.value.length,
            {
              type: typeText,
            }
          ),
          title: getLocalePluralText(
            "insights_landing_page.confirm_delete_title",
            selectedItems.value.length,
            {
              type: typeText,
            }
          ),
        },
        errorMessage: getLocaleText("insights_landing_page.delete_error", {
          type: typeText,
        }),
        ids: selectedItems.value,
        loadingMessage: getLocaleText("insights_landing_page.delete_loading", {
          type: typeText,
        }),
        successMessage: getLocaleText("insights_landing_page.delete_success", {
          type: typeText,
        }),
      });

      await actionHandler(async () => {
        await InsightsArchivedService.bulkRemove(
          selectedItemsMapped.value as InsightsLandingPageItemTarget[]
        );

        await rehandleLoadLandingPageItems(selectedItems.value);
      });

      editable.value = false;
    }

    async function handleUnarchive(item: LandingPageItem): Promise<void> {
      const target = isInsightsArchived(item) ? item.target : item;

      if (!isLandingPageItemTarget(target)) {
        return;
      }

      const typeText = getTypeText();

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Unarchive,
        errorMessage: getLocaleText("insights_landing_page.unarchive_error", {
          type: typeText,
        }),
        ids: [Number(target.id)],
        loadingMessage: getLocaleText(
          "insights_landing_page.unarchive_loading",
          {
            type: typeText,
          }
        ),
      });

      await actionHandler(async () => {
        await InsightsArchivedService.unarchive({
          id: Number(target.id),
          type: isStreamReport(target)
            ? "StreamReport"
            : "InstantInsightsReport",
        });

        await loadOrganisationUsage();
        await rehandleLoadLandingPageItems([Number(target.id)]);
      });
    }

    async function handleBulkUnarchive(): Promise<void> {
      if (type !== InsightsLandingPageType.Archived) {
        return;
      }

      const typeText = getTypeText(selectedItems.value.length);

      const actionHandler = getInsightsActionHandler({
        action: InsightsAction.Unarchive,
        errorMessage: getLocaleText("insights_landing_page.unarchive_error", {
          type: typeText,
        }),
        ids: selectedItems.value,
        loadingMessage: getLocalePluralText(
          "insights_landing_page.unarchive_loading",
          selectedItems.value.length,
          {
            type: typeText,
          }
        ),
        successMessage: getLocalePluralText(
          "insights_landing_page.unarchive_success",
          selectedItems.value.length,
          {
            type: typeText,
          }
        ),
      });

      await actionHandler(async () => {
        const ids = selectedItemsMapped.value;

        await InsightsArchivedService.bulkUnarchive(
          ids as InsightsLandingPageItemTarget[]
        );

        await loadOrganisationUsage();
        await rehandleLoadLandingPageItems(selectedItems.value);
      });

      editable.value = false;
    }

    function handleProcessedDownloads(): void {
      const disabledRegenerateIds =
        disabledActions.value[InsightsAction.Regenerate] || [];

      const processedIds = disabledRegenerateIds.filter(
        (id) =>
          !downloadsProcessing.value.some(
            (download) => id === Number(download.id)
          )
      );

      removeDisabledAction(InsightsAction.Regenerate, processedIds);
    }

    function handleResetStore(): void {
      editable.value = false;
      search.value = "";
      selectedItems.value = [];
      landingPageItems.value = initLandingPageItems();
      recentLandingPageItemsCache.value = [];
      dashboardTab.value = "all";
    }

    watch(dashboardTab, handleLoadLandingPageItems);

    return {
      // State
      dashboardTab,
      disabledActions,
      editable,
      landingPageItems,
      landingPageItemsAbortController,
      loadingActions,
      recentLandingPageItems,
      recentLandingPageItemsCache,
      search,
      selectedItems,

      // Getters
      downloadsProcessing,

      // Actions
      handleArchive,
      handleBulkArchive,
      handleBulkDelete,
      handleBulkDuplicate,
      handleBulkFavorite,
      handleBulkRegenerate,
      handleBulkRemoveFavorite,
      handleBulkShare,
      handleBulkUnarchive,
      handleDelete,
      handleDuplicate,
      handleGeneratePdf,
      handleGenerateScheduledReport,
      handleGenerateXlsx,
      handleLoadLandingPageItems,
      handleProcessedDownloads,
      handleRegenerate,
      handleResetStore,
      handleSearchLandingPageItems,
      handleShare,
      handleToggleFavorite,
      handleUnarchive,
      handleUpdatePagination,
      handleUpdateQuery,
      handleUpdateSortBy,
    };
  });
};

export default useInsightsLandingPageStoreFactory;
